import { cn } from '$app/utils'
import AppEnvironment from '$components/AppEnvironment'
import AppLogo from '$components/AppLogo'
import Loading from '$components/Loading'
import { useRouteSummary } from '$contexts/RouteContext/hooks'
import { useApiListingParams, useOS } from '$hooks/actions'
import { useAppListOptionService } from '$hooks/services'
import { useAppDetailStore } from '$hooks/stores'
import { redirect, ROUTE_NAMES } from '$router/config'
import { Spinner } from '@genie-fintech/ui/components'
import { BaseText } from '@genie-fintech/ui/components/fields'
import { Icon } from '@genie-fintech/ui/icons'
import * as Dialog from '@radix-ui/react-dialog'
import { FormEvent, useCallback, useEffect, useRef, useState } from 'react'
import { appNameStyle, container, subContainer } from './styles.css'
import { useBoolean } from 'ahooks'

const AppSwitcher = () => {
  const [open, { setTrue: setOpen, setFalse: setClose }] = useBoolean()

  const [activeIndex, setActiveIndex] = useState<number | null>(null)

  const {
    route: { params }
  } = useRouteSummary()

  const { appId } = params

  const { searchValue, updateSearchValue, debouncedSearchValue } =
    useApiListingParams()

  const cardRefs = useRef<(HTMLElement | null)[]>([])

  const appDetail = useAppDetailStore(state => state.appDetail)
  const { name, logo, is_published, environment, brand } = { ...appDetail }

  const { fetchAppSelectListAsync, fetchingAppSelectList, app_option_list } =
    useAppListOptionService()

  const { os } = useOS()

  useEffect(() => {
    const handleKeydown = (event: KeyboardEvent) => {
      const isMatchKey = event.key === 'k' || event.key === 'K'

      const action = () => {
        event.preventDefault()
        event.stopPropagation()
        setOpen()
      }

      if (os === 'mac') {
        if (event.metaKey && event.shiftKey && isMatchKey) {
          action()
        }
        return
      }

      if (os === 'windows' || os === 'linux') {
        if (event.ctrlKey && event.shiftKey && isMatchKey) {
          action()
        }
        return
      }
    }

    window.addEventListener('keydown', handleKeydown)

    return () => {
      window.removeEventListener('keydown', handleKeydown)
    }
  }, [os, setOpen])

  useEffect(() => {
    if (activeIndex === null) return
    cardRefs.current[activeIndex]?.scrollIntoView({
      behavior: 'smooth',
      block: 'center'
    })
  }, [activeIndex])

  useEffect(() => {
    if (!open) return

    if (!appId) return

    fetchAppSelectListAsync({
      q: debouncedSearchValue,
      per_page: 10,
      application_id: appId
    }).then(({ data }) => {
      if (!data.length) return
      setActiveIndex(0)
    })
  }, [open, appId, fetchAppSelectListAsync, debouncedSearchValue])

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'ArrowUp') {
        setActiveIndex(prevIndex =>
          prevIndex === null || prevIndex === 0
            ? app_option_list.length - 1
            : prevIndex - 1
        )
      } else if (event.key === 'ArrowDown') {
        setActiveIndex(prevIndex =>
          prevIndex === null || prevIndex === app_option_list.length - 1
            ? 0
            : prevIndex + 1
        )
      }
    },
    [app_option_list]
  )

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown)

    // Clean up the event listener on component unmount
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [handleKeyDown])

  const handleOnClickCard = useCallback(
    (appId: number) => {
      redirect(ROUTE_NAMES.APP_HOME, ({ params, ...options }) => ({
        ...options,
        params: { ...params, appId }
      }))
      setClose()
    },
    [setClose]
  )

  const handleOnEnter = useCallback(
    (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault()
      if (!app_option_list.length || activeIndex === null) return
      handleOnClickCard(app_option_list[activeIndex].id)
    },
    [app_option_list, activeIndex, handleOnClickCard]
  )

  return (
    <Dialog.Root open={open} onOpenChange={setClose}>
      <button className={container} onClick={setOpen}>
        <AppLogo imgUrl={logo?.url} isPublished={is_published} brand={brand} />

        {!name && <Spinner />}

        {name && (
          <article className={subContainer}>
            <span className={appNameStyle}>{name}</span>
            {environment && <AppEnvironment environment={environment} />}
          </article>
        )}

        <Icon namespace="UpDown" width={18} style={{ flexShrink: 0 }} />
      </button>
      <Dialog.Portal>
        <Dialog.Overlay className="DialogOverlay" onClick={setClose} />
        <Dialog.Content
          className="DialogContent max-w-2xl !bg-[--colors-area-medium] h-[500px]"
          aria-describedby=""
        >
          {fetchingAppSelectList && (
            <article className="flex-1 grid place-items-center">
              <Loading />
            </article>
          )}

          <article className="flex flex-col p-0 h-[500px] overflow-y-auto overflow-x-hidden relative no-scrollbar">
            <Dialog.DialogTitle className="sticky top-0 z-10">
              <form
                className="flex px-4 py-2 bg-[--colors-area-high]"
                onSubmit={handleOnEnter}
              >
                <BaseText
                  elementControlProps={{
                    className: 'border-transparent flex py-2 gap-x-2'
                  }}
                  affix={{
                    pre: <Icon namespace="Search" width={16} />,
                    post: searchValue ? (
                      <button
                        type="button"
                        onClick={() => updateSearchValue('')}
                      >
                        <Icon namespace="Cross" width={18} />
                      </button>
                    ) : undefined
                  }}
                  inputProps={{
                    placeholder: 'Enter App Name',
                    value: searchValue,
                    onChange: e => updateSearchValue(e.currentTarget.value)
                  }}
                />
              </form>
            </Dialog.DialogTitle>

            <article className="flex flex-col gap-2 px-4 py-2 flex-1">
              {!app_option_list.length && !fetchingAppSelectList && (
                <article className="flex items-center justify-center flex-1 text-[--colors-neutral-50]">
                  App Not Found!
                </article>
              )}

              {app_option_list.map((v, k) => {
                const isActive = k === activeIndex

                return (
                  <article
                    key={k}
                    className={cn(
                      'flex flex-col cursor-pointer gap-2 rounded-lg hover:bg-[--colors-neutral-10] duration-200',
                      isActive
                        ? 'border-2 border-[--colors-primary-80]'
                        : 'border-2 border-[--colors-neutral-10]'
                    )}
                    ref={el => (cardRefs.current[k] = el)}
                    onClick={() => {
                      handleOnClickCard(v.id)
                    }}
                  >
                    <article className="flex items-center gap-2 p-4 bg-[--colors-area-high] rounded-lg">
                      <AppLogo
                        isPublished={v.is_published}
                        imgUrl={v.logo?.url}
                        brand={v.brand}
                        size="big"
                      />

                      <article className="flex-1 flex flex-col">
                        <p className="text-sm font-medium truncate">{v.name}</p>

                        <AppEnvironment environment={v.environment} />
                      </article>

                      <article className="flex gap-1 items-center text-[--colors-text-light]">
                        <span className="text-sm font-medium">Switch</span>
                        <Icon namespace="Forward" width={18} />
                      </article>
                    </article>
                  </article>
                )
              })}
            </article>
          </article>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

export default AppSwitcher
